import { memo, useState } from "react";
import { AlertTriangle, ChevronDown, Pencil, Truck } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { OPS } from "@/constants/testIds";
import {
    addMonths, allowsDecimalOf, flagOf, fmt, fmtDay, fmtPct, fmtPeriod, fmtQuantity, fmtSigned, labelOf, pctOf,
    shiftDate, unitOf,
} from "./dkUtils";
import { closingOf, figuresOf, isPackItem, num, receivedOf } from "./dayModel";
import { QtyField } from "./widgets";

const S = OPS.dk.day;
const NO_DEST = "__none";
const label = "block text-[11px] font-bold text-[color:var(--ink-muted)]";

/** «من إغلاق أمس» / «من جرد سبتمبر ٢٠٢٦» / «معدّل يدوياً» / «لا يوجد رصيد افتتاحي». */
export function openingSourceText(line, date, t, lang) {
    const src = line?.opening_source || "none";
    const ref = line?.opening_ref || {};
    if (src === "override") return t("dk_opening_src_override");
    if (src === "prev_close") {
        if (ref.date && date && ref.date !== shiftDate(date, -1)) return fmt(t("dk_opening_src_prev_day"), { day: fmtDay(ref.date, lang) });
        return t("dk_opening_src_yesterday");
    }
    if (src === "stock_count" && date) {
        const parts = [fmt(t("dk_opening_src_count"), { month: fmtPeriod(addMonths(date.slice(0, 7), -1), lang) })];
        if (ref.status === "submitted") parts.push(t("dk_opening_count_submitted"));
        if (ref.auto_zero || (line?.warn || []).includes("auto_zero")) parts.push(t("dk_opening_auto_zero"));
        return parts.join(" · ");
    }
    return t("dk_opening_none");
}

/** A warning code's label: the server's (`scope.labels.warn`), else ours. */
export function warnText(code, scope, t, lang) {
    if ((scope?.labels?.warn || []).some((w) => w.key === code)) return labelOf(scope.labels.warn, code, lang);
    const key = `dk_warn_${code}`;
    const s = t(key);
    return s === key ? code : s;
}

function Figure({ title, testId, value, unit, sub, tone = "", data }) {
    return (
        <div data-testid={testId} {...data}
             className={`min-w-0 rounded-[var(--radius-md)] border px-2 py-1.5 ${tone || "border-[color:var(--border-soft)] bg-[color:var(--bg-surface-2)]"}`}>
            <p className="truncate text-[10px] font-bold text-[color:var(--ink-muted)]">{title}</p>
            <p className="mt-0.5 truncate text-sm font-extrabold text-[color:var(--ink)]">
                {value === "" || value === null || value === undefined
                    ? <span className="text-[color:var(--ink-faint)]">—</span>
                    : <><bdi dir="ltr">{value}</bdi> <span className="text-[10px] font-semibold text-[color:var(--ink-muted)]">{unit}</span></>}
            </p>
            {sub ? <p className="truncate text-[10px] leading-tight text-[color:var(--ink-faint)]">{sub}</p> : null}
        </div>
    );
}

/** Read-only quantity: «٢ كرتون + ٣ حبة = ٥١ حبة», «١٢٫٥ كيلو», or «—». */
function QtyText({ item, total, packs, loose, testId, counted = true }) {
    const { lang, t } = useI18n();
    const unit = unitOf(item, t);
    if (!counted || total === null || total === undefined) {
        return <span data-testid={testId} data-counted="false" className="text-sm font-semibold text-[color:var(--ink-faint)]">{t("dk_not_counted")}</span>;
    }
    return (
        <span data-testid={testId} data-counted="true" data-value={total} className="text-sm font-bold text-[color:var(--ink)]">
            {isPackItem(item) && packs ? (
                <span className="me-1 text-xs font-medium text-[color:var(--ink-muted)]">
                    <bdi dir="ltr">{fmtQuantity(packs, lang)}</bdi> {item.pack_unit}
                    {loose ? <> + <bdi dir="ltr">{fmtQuantity(loose, lang)}</bdi> {unit}</> : null}
                    {" = "}
                </span>
            ) : null}
            <bdi dir="ltr">{fmtQuantity(total, lang)}</bdi> {unit}
        </span>
    );
}

/** Packs + loose for an item with a pack helper, with the total chip; one input otherwise. */
function QtyInputs({ item, kind, entry, onChange, onEnter, invalid, title }) {
    const { t, tn, lang } = useI18n();
    const unit = unitOf(item, t);
    const dec = allowsDecimalOf(item);
    const id = item.id;
    if (!isPackItem(item)) {
        const key = kind === "received" ? "r" : "c";
        return (
            <label className="block min-w-0 space-y-1">
                <span className={label}>{title}</span>
                <QtyField testId={kind === "received" ? S.received(id) : S.closing(id)} value={entry?.[key]} unit={unit}
                          allowsDecimal={dec} invalid={invalid(kind)} label={`${tn(item)} — ${title}`}
                          placeholder={kind === "received" ? "0" : undefined}
                          onChange={(v) => onChange({ ...entry, [key]: v })} onEnter={onEnter} />
            </label>
        );
    }
    const [pk, lk] = kind === "received" ? ["rp", "rl"] : ["cp", "cl"];
    const total = kind === "received" ? receivedOf(item, entry) : closingOf(item, entry);
    const counted = kind === "received" ? !!total : total !== null;
    return (
        <div className="min-w-0 space-y-1">
            <div className="flex items-center justify-between gap-2">
                <span className={label}>{title}</span>
                <span data-counted={counted ? "true" : "false"}
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${
                          counted
                              ? "bg-[color:var(--brand-orange-soft)] text-[color:var(--brand-orange-soft-fg)]"
                              : "bg-[color:var(--bg-surface-2)] text-[color:var(--ink-faint)]"
                      }`}>
                    {counted ? <>= <bdi dir="ltr">{fmtQuantity(total, lang)}</bdi> {unit}</>
                        : kind === "received" ? <>= <bdi dir="ltr">{fmtQuantity(0, lang)}</bdi></> : t("dk_not_counted")}
                </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
                <QtyField testId={kind === "received" ? S.receivedPacks(id) : S.closingPacks(id)} value={entry?.[pk]}
                          unit={item.pack_unit} allowsDecimal={false} invalid={invalid(`${kind}_packs`)}
                          label={`${tn(item)} — ${title} — ${t("dk_packs")}`} placeholder={t("dk_packs")}
                          onChange={(v) => onChange({ ...entry, [pk]: v })} onEnter={onEnter} />
                <QtyField testId={kind === "received" ? S.receivedLoose(id) : S.closingLoose(id)} value={entry?.[lk]}
                          unit={unit} allowsDecimal={dec} invalid={invalid(`${kind}_loose`)}
                          label={`${tn(item)} — ${title} — ${t("dk_loose")}`} placeholder={t("dk_loose")}
                          onChange={(v) => onChange({ ...entry, [lk]: v })} onEnter={onEnter} />
            </div>
        </div>
    );
}

/**
 * One item card of «اليوم». Editable: received and closing (packs + loose when the
 * item has a pack helper), and under «المزيد» the quantity sent to another branch
 * and a note. Always: the opening and where it came from (with «تعديل» for an
 * override), and the computed used / theoretical / variance — the server's for a
 * saved line, a live preview while the line has unsaved edits.
 */
export const DayLine = memo(function DayLine({
    item, line, entry, dirty, errors, editable, scope, date, destinations, locationId, dataState,
    onChange, onEnter, onOverride,
}) {
    const { t, tn, lang, dir } = useI18n();
    const ln = line || {};
    const unit = unitOf(item, t);
    const pack = isPackItem(item);
    const errs = errors || [];
    const errFields = new Set(errs.map((e) => e.field).filter(Boolean));
    const invalid = (field) => errFields.has(field) || (field.startsWith("received") && errFields.has("received"))
        || (field.startsWith("closing") && errFields.has("closing"));
    const [more, setMore] = useState(() => (num(entry?.s) || 0) > 0 || !!(entry?.n || "").trim());

    // System figures (by sales, variance, flags) are for the operations manager
    // only; the server leaves them out for everyone else (dk_core.can_see_system).
    const system = !!scope?.caps?.can_see_system;
    const fig = figuresOf(item, ln, entry, dirty);
    const flag = !system ? false : fig.preview
        ? flagOf({ unexplained: fig.unexplained, theoretical: fig.theoretical, unitClass: item.unit_class,
                   itemMinMilli: item.flag_min_milli, settings: scope?.settings })
        : fig.flag;
    const pct = pctOf(fig.unexplained, fig.theoretical, fig.preview ? null : ln.pct);
    const closing = editable ? closingOf(item, entry) : ln.closing ?? null;
    const counted = closing !== null && closing !== undefined;

    // warnings: the stored ones for a saved line; W1 / W3 recomputed for an edited one
    const warns = fig.preview
        ? [
            ...(fig.used !== null && fig.used < 0 ? ["W1"] : []),
            // in milli, as the server does (a float remainder of 10.8 % 5.4 is not 0)
            ...(pack && Math.round(receivedOf(item, entry) * 1000) % Math.round(Number(item.pack_size) * 1000) ? ["W3"] : []),
        ]
        : (ln.warn || []).filter((w) => w !== "auto_zero");

    const sentQty = editable ? num(entry?.s) || 0 : Number(ln.sent || 0);
    const sentTo = editable ? entry?.to : ln.sent_to;
    const destName = (id) => {
        const d = (destinations || []).find((x) => x.id === id);
        return d ? tn(d) : t("dk_sent_to_other");
    };
    const destOptions = (destinations || []).filter((d) => d.id !== locationId);

    const set = (next) => onChange(item, next);
    // why there is no «حسب المبيعات» yet, in a word (the full sentence is above the cards)
    const theoSub = fig.theoretical === null
        ? t(dataState === "not_synced" ? "dk_theo_none" : dataState === "no_pos_id" ? "dk_theo_no_pos" : "dk_theo_unlinked")
        : null;
    const explainedN = Number(fig.explained || 0);
    const varianceSub = pct !== null ? fmtPct(pct, lang) : null;

    return (
        <article data-testid={S.card(item.id)} data-counted={counted ? "true" : "false"} data-dirty={dirty ? "true" : "false"}
                 data-flag={flag ? "true" : "false"}
                 className={`space-y-3 rounded-[var(--radius-lg)] border p-3 ${
                     errs.length ? "border-[color:var(--danger)] bg-[color:rgba(220,38,38,0.04)]"
                         : flag ? "border-[color:var(--danger-soft-fg)]/40 bg-[color:var(--bg-surface)]"
                             : "border-[color:var(--border-soft)] bg-[color:var(--bg-surface)]"
                 }`}>
            {/* name, unit, pack, flag */}
            <header className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <h3 className="flex items-start gap-1.5 text-sm font-bold leading-snug text-[color:var(--ink)]">
                        {dirty ? <span aria-hidden className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[color:var(--brand-orange)]" /> : null}
                        <span className="min-w-0 break-words">{tn(item)}</span>
                    </h3>
                    <p className="mt-0.5 text-[11px] text-[color:var(--ink-faint)]">
                        {unit}
                        {pack ? <> · <bdi>{fmt(t("dk_pack_hint"), { pack: item.pack_unit, n: fmtQuantity(item.pack_size, lang), unit })}</bdi></> : null}
                    </p>
                </div>
                {flag ? (
                    <span data-testid={S.flag(item.id)} data-preview={fig.preview ? "true" : "false"}
                          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[color:var(--danger-soft)] px-2 py-1 text-[11px] font-bold text-[color:var(--danger-soft-fg)]">
                        <AlertTriangle className="h-3.5 w-3.5" />{t("dk_flag")}
                    </span>
                ) : null}
            </header>

            {/* opening + its source */}
            <div data-testid={S.opening(item.id)} data-source={ln.opening_source || "none"} data-value={ln.opening ?? ""}
                 className="flex items-start justify-between gap-2 rounded-[var(--radius-md)] bg-[color:var(--bg-surface-2)] px-2.5 py-2">
                <div className="min-w-0">
                    <p className="text-[11px] font-bold text-[color:var(--ink-muted)]">{t("dk_opening")}</p>
                    <p className="text-sm font-extrabold text-[color:var(--ink)]">
                        {ln.opening === null || ln.opening === undefined
                            ? <span className="text-xs font-semibold text-[color:var(--warning-soft-fg)]">{t("dk_opening_none")}</span>
                            : <><bdi dir="ltr">{fmtQuantity(ln.opening, lang)}</bdi> <span className="text-xs font-semibold text-[color:var(--ink-muted)]">{unit}</span></>}
                    </p>
                    {ln.opening !== null && ln.opening !== undefined ? (
                        <p className="text-[11px] leading-snug text-[color:var(--ink-faint)]">{openingSourceText(ln, date, t, lang)}</p>
                    ) : null}
                    {ln.opening_source === "override" && ln.o_reason ? (
                        <p className="text-[11px] leading-snug text-[color:var(--ink-faint)]">«{ln.o_reason}»</p>
                    ) : null}
                </div>
                {editable && onOverride ? (
                    <button type="button" data-testid={S.openingOverride(item.id)} onClick={() => onOverride(item)}
                            className="inline-flex h-9 shrink-0 items-center gap-1 rounded-full border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] px-3 text-[11px] font-semibold text-[color:var(--ink)] hover:bg-[color:var(--brand-orange-soft)]">
                        <Pencil className="h-3.5 w-3.5" />{t("dk_override_btn")}
                    </button>
                ) : null}
            </div>

            {/* received / closing */}
            {editable ? (
                pack ? (
                    <div className="space-y-3">
                        <QtyInputs item={item} kind="received" entry={entry} onChange={set} onEnter={onEnter} invalid={invalid} title={t("dk_received")} />
                        <QtyInputs item={item} kind="closing" entry={entry} onChange={set} onEnter={onEnter} invalid={invalid} title={t("dk_closing")} />
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-2">
                        <QtyInputs item={item} kind="received" entry={entry} onChange={set} onEnter={onEnter} invalid={invalid} title={t("dk_received")} />
                        <QtyInputs item={item} kind="closing" entry={entry} onChange={set} onEnter={onEnter} invalid={invalid} title={t("dk_closing")} />
                    </div>
                )
            ) : (
                <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
                    <div className="min-w-0">
                        <dt className={label}>{t("dk_received")}</dt>
                        <dd><QtyText item={item} testId={S.received(item.id)} total={Number(ln.received || 0)}
                                     packs={ln.received_packs} loose={ln.received_loose} /></dd>
                    </div>
                    <div className="min-w-0">
                        <dt className={label}>{t("dk_closing")}</dt>
                        <dd><QtyText item={item} testId={S.closing(item.id)} total={ln.closing} counted={counted}
                                     packs={ln.closing_packs} loose={ln.closing_loose} /></dd>
                    </div>
                    {sentQty ? (
                        <div className="col-span-2 min-w-0">
                            <dt className={label}>{t("dk_sent")}</dt>
                            <dd data-testid={S.sent(item.id)} data-value={sentQty} className="text-sm font-bold text-[color:var(--ink)]">
                                <bdi dir="ltr">{fmtQuantity(sentQty, lang)}</bdi> {unit}
                                {sentTo ? <span className="ms-1 text-xs font-medium text-[color:var(--ink-muted)]">{dir === "rtl" ? "←" : "→"} {destName(sentTo)}</span> : null}
                            </dd>
                        </div>
                    ) : null}
                    {ln.note ? (
                        <div className="col-span-2 min-w-0">
                            <dt className={label}>{t("dk_note")}</dt>
                            <dd className="break-words text-xs text-[color:var(--ink)]">{ln.note}</dd>
                        </div>
                    ) : null}
                </dl>
            )}

            {/* «المزيد»: sent to another branch + note */}
            {editable ? (
                <div>
                    <button type="button" data-testid={S.more(item.id)} aria-expanded={more} onClick={() => setMore((v) => !v)}
                            className="inline-flex h-8 items-center gap-1 text-[11px] font-bold text-[color:var(--brand-brown)]">
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-150 ${more ? "rotate-180" : ""}`} />
                        {t("dk_more")}
                        {!more && sentQty ? (
                            <span className="ms-1 inline-flex items-center gap-1 font-semibold text-[color:var(--ink-muted)]">
                                <Truck className="h-3.5 w-3.5" /><bdi dir="ltr">{fmtQuantity(sentQty, lang)}</bdi>
                            </span>
                        ) : null}
                    </button>
                    {more ? (
                        <div className="mt-1 space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                                <label className="block min-w-0 space-y-1">
                                    <span className={label}>{t("dk_sent")}</span>
                                    <QtyField testId={S.sent(item.id)} value={entry?.s} unit={unit} allowsDecimal={allowsDecimalOf(item)}
                                              invalid={invalid("sent")} label={`${tn(item)} — ${t("dk_sent")}`} placeholder="0"
                                              onChange={(v) => set({ ...entry, s: v })} onEnter={onEnter} />
                                </label>
                                <div className="min-w-0 space-y-1">
                                    <span className={label}>{t("dk_sent_to")}</span>
                                    <Select value={entry?.to || NO_DEST} disabled={!sentQty}
                                            onValueChange={(v) => set({ ...entry, to: v === NO_DEST ? "" : v })}>
                                        <SelectTrigger data-testid={S.sentTo(item.id)}
                                                       className={`h-11 w-full text-sm ${invalid("sent_to") ? "border-[color:var(--danger)]" : ""}`}>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value={NO_DEST}>{t("dk_sent_to_none")}</SelectItem>
                                            {destOptions.map((d) => <SelectItem key={d.id} value={d.id}>{tn(d)}</SelectItem>)}
                                            {entry?.to && !destOptions.some((d) => d.id === entry.to)
                                                ? <SelectItem value={entry.to}>{destName(entry.to)}</SelectItem> : null}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <label className="block min-w-0 space-y-1">
                                <span className={label}>{t("dk_note")}</span>
                                <Input data-testid={S.note(item.id)} value={entry?.n ?? ""} maxLength={300} dir="auto" autoComplete="off"
                                       onChange={(e) => set({ ...entry, n: e.target.value })}
                                       placeholder={t("dk_note_ph")}
                                       className={`h-11 text-base ${invalid("note") ? "border-[color:var(--danger)]" : ""}`} />
                            </label>
                        </div>
                    ) : null}
                </div>
            ) : null}

            {/* computed: used · by sales · variance (the last two for the operations manager only) */}
            <div className={`grid gap-1.5 ${system ? "grid-cols-3" : "grid-cols-1"}`}>
                <Figure title={t("dk_used")} testId={S.used(item.id)} unit={unit}
                        value={fig.used === null ? "" : fmtQuantity(fig.used, lang)}
                        data={{ "data-value": fig.used ?? "", "data-preview": fig.preview ? "true" : "false" }}
                        sub={ln.multi_day ? fmt(t("dk_multi_day"), { n: fmtQuantity(ln.multi_day, lang) })
                            : fig.used === null ? (ln.opening === null || ln.opening === undefined ? t("dk_used_no_opening")
                                : !counted ? t("dk_used_no_closing") : null) : null} />
                {system ? (
                    <Figure title={t("dk_theo")} testId={S.theo(item.id)} unit={unit}
                            value={fig.theoretical === null ? "" : fmtQuantity(fig.theoretical, lang)}
                            data={{ "data-value": fig.theoretical ?? "" }} sub={theoSub} />
                ) : null}
                {system ? (
                    <Figure title={t("dk_variance")} testId={S.variance(item.id)} unit={unit}
                            value={fig.unexplained === null ? "" : fmtSigned(fig.unexplained, lang)}
                            data={{ "data-value": fig.unexplained ?? "", "data-variance": fig.variance ?? "",
                                    "data-flag": flag ? "true" : "false" }}
                            tone={flag ? "border-[color:var(--danger-soft-fg)]/30 bg-[color:var(--danger-soft)]" : ""}
                            sub={varianceSub} />
                ) : null}
            </div>
            {system && explainedN && fig.unexplained !== null ? (
                <p className="text-[11px] text-[color:var(--ink-faint)]">
                    {fmt(t("dk_explained_line"), { q: fmtQuantity(explainedN, lang), unit, v: fmtSigned(fig.variance, lang) })}
                </p>
            ) : null}

            {warns.length ? (
                <ul data-testid={S.warn(item.id)} className="space-y-0.5">
                    {warns.map((w) => (
                        <li key={w} data-code={w} className="flex items-start gap-1.5 text-[11px] font-semibold text-[color:var(--warning-soft-fg)]">
                            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />{warnText(w, scope, t, lang)}
                        </li>
                    ))}
                </ul>
            ) : null}
            {errs.length ? (
                <ul data-testid={S.lineError(item.id)} className="space-y-0.5">
                    {errs.map((e, i) => (
                        <li key={i} data-field={e.field || ""} className="text-[11px] font-semibold text-[color:var(--danger)]">
                            {lang === "ar" ? e.message_ar || e.message_en : e.message_en || e.message_ar}
                        </li>
                    ))}
                </ul>
            ) : null}
        </article>
    );
});
